import React from 'react';

import CoverSheet from '../general/coverSheet';
import ETAnomalyPDF from '../et/ETAnomalyPDF';
import PDFInfoSheet from '../../../themed/pdf/pdfInfoSheet';
import PDFPhotoPage from '../../../themed/pdf/pdfPhotoPage';
import PAUTInspectionReport from './pautInspectionReport';
import PAUTSummary from './pautSummary';
import PAUTMTIReport from './pautMTIReport';
import PAUTWeldScan from './pautWeldScan';
import PDFPage from '../../../themed/pdf/pdfPage';

import withPDF from '../../../hoc/withPDF';

import * as models from '../../../../utils/models';
import * as utils from '../../../../utils/utilities';

interface PAUTReportProps {
	anomalyData: models.VTEquipmentAnomalyData[];
	coverSheetData: models.PAUTCoverData;
	isoImages: models.PDFImageProps[];
	photoData: models.PDFImageProps[];
	mtiReportData: models.MTInspectionReportData;
	projectDetails: models.Project;
	pautOverview: models.PAUTOverview;
	pautInspectionItem: models.PAUTInspectionItem;
	pautItem: models.PAUTItem;
	summaryData: models.PAUTSummaryData;
	weldScanOverview: models.PAUTWeldScanOverview;
}

const PAUTReport = (props: PAUTReportProps): JSX.Element => {
	const { documentNumber, pidImages } = props.projectDetails;

	const standardCriteriaItems: models.CoverDataItem[] = [
		{
			label: '1. Qualification of NDT Personnel',
			value: props.coverSheetData?.ndtQualification,
		},
		{
			label: '2. Non Destructive Testing',
			value: props.coverSheetData?.nonDestructiveTesting,
		},
		{
			label: '3. Process Piping',
			value: props.coverSheetData?.processPiping,
		},
		{
			label: '4. Upstream Process Piping Welding and Inspection',
			value: props.coverSheetData?.upstreamProcessPiping,
		},
	];

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverSheetData?.scopeOfWork,
		},
		{
			label: '2. Visual Examination',
			value: props.coverSheetData?.visualExamination,
		},
		{
			label: '3.  Phased Array Procedure For Weld Scan',
			value: props.coverSheetData?.phasedProcedure,
		},
		{
			label: '4.  Magnetic Particle Examination',
			value: props.coverSheetData?.mpExamination,
		},
		{
			label: '5.  Ultrasonic Testing Procedure for Flaw Detection (Shear Wave)',
			value: props.coverSheetData?.utProcedure,
		},
		{
			label: '6. Circute No',
			value: props.coverSheetData?.circuteNo,
		},
	];

	const photoRecord = utils.getChunkedData(props.photoData, 4);
	const pageSubtitle: string = 'Document Number: ' + documentNumber;

	return (
		<React.Fragment>
			<PDFInfoSheet
				companyUrl={props.projectDetails.organisation.organisationLogo}
				documentNo={props.projectDetails.documentNumber}
				clientName={props.projectDetails.organisationName}
				facility={props.projectDetails.facilityName}
				reportTitle={props.projectDetails.projectTitle}
			/>

			<PDFPage
				pageDetails={utils.getPageDetails('Cover Sheet', pageSubtitle)}
				projectDetails={props.projectDetails}
			>
				<CoverSheet
					overviewData={overviewData}
					referenceDocuments={referenceDocuments}
					standardCriteriaItems={standardCriteriaItems}
					note="Refer to work instruction for inspection requirement"
				/>
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Summary Report')}
				projectDetails={props.projectDetails}
			>
				<PAUTSummary {...props.summaryData} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('P & ID Sheet')}
				projectDetails={props.projectDetails}
			>
				<PDFPhotoPage photos={utils.getPIDImages(pidImages)} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Isometric Drawing Sheet')}
				projectDetails={props.projectDetails}
			>
				<PDFPhotoPage photos={props.isoImages} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails(
					'Phased Array Ultrasonic (PAUT) Inspection Report'
				)}
				projectDetails={props.projectDetails}
			>
				<PAUTInspectionReport
					pautInspectionItem={props.pautInspectionItem}
					pautOverview={props.pautOverview}
					projectDetails={props.projectDetails}
					pautItem={props.pautItem}
				/>
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails(
					'Magnetic Particle Inspection Report',
					'Overview'
				)}
				projectDetails={props.projectDetails}
			>
				<PAUTMTIReport {...props.mtiReportData} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails(
					'Weld Scan - Shear Wave',
					'Equipment and Calibration Details'
				)}
				projectDetails={props.projectDetails}
			>
				<PAUTWeldScan {...props.weldScanOverview} />
			</PDFPage>

			<ETAnomalyPDF data={props.anomalyData} project={props.projectDetails} />

			{photoRecord.map((photos: models.PDFImageProps[], index: number) => (
				<PDFPage
					key={photos[index].src}
					enableTitlePage={index === 0}
					pageDetails={utils.getPageDetails('Photographic Record')}
					projectDetails={props.projectDetails}
				>
					<PDFPhotoPage photos={photos} />
				</PDFPage>
			))}
		</React.Fragment>
	);
};

const overviewData: models.CoverDataOverview[] = [
	{ report: '1. Cover Sheet', required: 'Yes', page: '2' },
	{ report: '2. Summary Sheet', required: 'Yes', page: '3' },
	{
		report: '3. P & ID Sheet',
		required: 'Yes',
		page: '4',
	},
	{
		report: '4. Isometric Drawing Sheet',
		required: 'Yes',
		page: '4',
	},
	{
		report: '5. Phased Array Result',
		required: 'Yes',
		page: '4',
	},
	{
		report: '6. Magnetic Particle Inspection Report',
		required: 'Yes',
		page: '4',
	},
	{
		report: '7. Shearwave Inspection Report (Weld thick =or > 10mm)',
		required: 'Yes',
		page: '4',
	},
	{
		report: '8. Visual Inspection',
		required: 'Yes',
		page: '4',
	},
	{
		report: '9. Photographic Record',
		required: 'Yes',
		page: '4',
	},
];

export default withPDF(PAUTReport);
